// src/modules/app-views/_apps/appmulti/plans/pages/Init/components/PlansFilterPopover/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************

import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';
import plansService from 'modules/app-views/_apps/appmulti/plans/service';
import { useCallback } from 'react';
import translateX from 'utils/translateX';

import { Button, Select } from 'antd';

import './PlansFilterPopover.less';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const PRICE_TYPE_OPTIONS = plansService.getPriceTypes();

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const PlansFilterPopover = ({ onRequestClose, onOpenDrawer }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { setFilter, clearFilters, query } = useListContext() || {};
  const storedParameters = useZustand('app', 'parameters');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. DERIVED VALUES — read directly from context (no local state)
  // ─────────────────────────────────────────────────────────────────────────────
  const operatorsList = storedParameters?.modules?.appmultiOperators?.operatorsList || [];
  const operatorOptions = operatorsList.map(op => ({ value: op._id, label: op.name }));

  const operatorValue = (() => {
    const v = query?.filter?.operatorId;
    return Array.isArray(v) ? v[0] : (v ?? undefined);
  })();

  const priceTypeValue = (() => {
    const v = query?.filter?.priceType;
    return Array.isArray(v) ? v[0] : (v ?? undefined);
  })();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS — apply immediately on change (no deferred apply)
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOperatorChange = useCallback(
    value => {
      setFilter?.('operatorId', value != null ? [value] : undefined);
    },
    [setFilter]
  );

  const handlePriceTypeChange = useCallback(
    value => {
      setFilter?.('priceType', value != null ? [value] : undefined);
    },
    [setFilter]
  );

  const handleClear = useCallback(() => {
    clearFilters?.();
    onRequestClose?.();
  }, [clearFilters, onRequestClose]);

  const handleAdvancedFilters = useCallback(() => {
    onRequestClose?.();
    onOpenDrawer?.();
  }, [onRequestClose, onOpenDrawer]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="plans-filter-popover">
      <div className="plans-filter-popover__title">{translateX('quick_filters')}</div>

      <div className="plans-filter-popover__row">
        <div className="plans-filter-popover__field">
          <div className="plans-filter-popover__label">{translateX('operator')}</div>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            size="middle"
            value={operatorValue}
            onChange={handleOperatorChange}
            options={operatorOptions}
            placeholder={translateX('operator')}
          />
        </div>

        <div className="plans-filter-popover__field">
          <div className="plans-filter-popover__label">{translateX('price_type')}</div>
          <Select
            allowClear
            size="middle"
            value={priceTypeValue}
            onChange={handlePriceTypeChange}
            options={PRICE_TYPE_OPTIONS.map(o => ({
              value: o.value,
              label: translateX(o.label),
            }))}
            placeholder={translateX('price_type')}
          />
        </div>
      </div>

      <div className="plans-filter-popover__divider">
        <Button
          type="link"
          size="small"
          className="plans-filter-popover__advanced-btn"
          onClick={handleAdvancedFilters}
        >
          {`${translateX('advanced_filters')} →`}
        </Button>
        <div className="plans-filter-popover__actions">
          <Button size="small" onClick={handleClear}>
            {translateX('clear')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PlansFilterPopover;
